import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { CalendarCreateQuery } from "./__generated__/CalendarCreateQuery.graphql";
import { CalendarAdminComponent } from "./CalendarAdminComponent";
import {
  CalendarCreateScreenProps,
  CalendarParamList,
} from "./CalendarCreateNavigation";
import { CalendarAddScreen } from "./CalendarAdd";
import { CalendarSubscribeScreen } from "./CalendarSubscribe";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

function CalendarCreateScreen(
  props: CalendarCreateScreenProps<"CalendarCreateScreen">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <CalendarCreateScreenComponent {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function CalendarCreateScreenComponent(
  props: CalendarCreateScreenProps<"CalendarCreateScreen"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<CalendarCreateQuery>(
    graphql`
      query CalendarCreateQuery {
        store {
          ...CalendarAdminComponent_store
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );
  const [createCalendar, createCalendarLoading] = useMutation(graphql`
    mutation CalendarCreateMutation($input: StoreCalendarCreateInput!) {
      store {
        createCalendar(input: $input) {
          id
          title
          color
        }
      }
    }
  `);

  const [title, setTitle] = useState("");
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [color, setColor] = useState<string | undefined>();
  const intl_tokens = useIntlTokens();
  useNavigationHeader({
    type: "CREATE",
    onCancel: () => props.navigation.goBack(),
    onSave: () =>
      createCalendar({
        variables: {
          input: {
            title,
            teamIds,
            color,
          },
        },
        onCompleted: () =>
          props.navigation.popTo("StoreAdmin", {
            screen: "CalendarsAdmin",
          }),
      }),
    saveLoading: createCalendarLoading,
    title: intl_tokens.CreateCalendar,
    disabled: title.length === 0 || color === undefined,
    defaultContentStyle: true,
  });

  return (
    <CalendarAdminComponent
      title={title}
      setTitle={setTitle}
      teamIds={teamIds}
      setTeamIds={setTeamIds}
      store={data.store}
      color={color}
      setColor={setColor}
      iCalUrl={null}
    />
  );
}

const Stack = createNativeStackNavigator<CalendarParamList>();

export const CalendarCreate = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CalendarCreate">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    return (
      <Stack.Navigator initialRouteName="CalendarAddScreen">
        <Stack.Screen
          name="CalendarAddScreen"
          component={CalendarAddScreen}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: intl_tokens.AddCalendar,
            defaultContentStyle: true,
          })}
        />
        <Stack.Screen
          name="CalendarCreateScreen"
          component={CalendarCreateScreen}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: intl_tokens.CreateCalendar,
            defaultContentStyle: true,
          })}
        />
        <Stack.Screen
          name="CalendarSubscribeScreen"
          component={CalendarSubscribeScreen}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: intl_tokens.SubscribeToCalendar,
            defaultContentStyle: true,
          })}
        />
      </Stack.Navigator>
    );
  }
);
